// components/Benchmark/BenchmarkAnalyticsPage.jsx — Pricing Analytics Dashboard
import React, { useMemo, useState } from "react";
import { Button, Field, Select, Spinner, Text } from "@fluentui/react-components";
import {
  ArrowLeft24Regular, ArrowSync24Regular,
  DataBarVertical24Regular, ArrowTrendingLines24Regular,
  Warning24Regular,
} from "@fluentui/react-icons";
import { useNavigate } from "react-router-dom";
import { useBenchmarkDB } from "../../hooks/useBenchmarkDB";
import { BENCHMARK_CATEGORIES, PHASE_LABELS } from "../../services/benchmarkService";

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtNum(n, decimals = 0) {
  if (!n && n !== 0) return "—";
  return Number(n).toLocaleString("en-NG", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

const PHASE_STYLE = {
  1: { color: "#005A9E", bg: "#EBF3FF" },
  2: { color: "#006840", bg: "#EAF6EA" },
  3: { color: "#7B5200", bg: "#FFF4EC" },
};

const CAT_COLORS = [
  "#0066FF","#006E6E","#7B2D8B","#C4262E","#D1521A","#637074",
  "#005A9E","#006840","#B87000","#8B3A3A","#2D6A8B","#4A7B4A","#6B4A8B","#8B6A2D",
];

function catColor(cat) {
  const idx = BENCHMARK_CATEGORIES.indexOf(cat);
  return CAT_COLORS[idx >= 0 ? idx : 0];
}

// ── Horizontal Bar Chart (SVG) ─────────────────────────────────────────────────

function HBarChart({ data, valueLabel, height = 320, formatValue }) {
  if (!data || data.length === 0) return <EmptyChart />;
  const maxVal = Math.max(...data.map(d => Math.abs(d.value)), 1);
  const rowH   = Math.floor((height - 20) / data.length);
  const barH   = Math.min(rowH - 10, 28);
  const labelW = 140;
  const chartW = 400;
  const svgW   = labelW + chartW + 80;
  const svgH   = data.length * rowH + 20;
  const fmt    = formatValue || (v => fmtNum(v, 1));

  return (
    <svg viewBox={`0 0 ${svgW} ${svgH}`} style={{ width: "100%", overflow: "visible" }}>
      {/* Grid lines */}
      {[0, 0.25, 0.5, 0.75, 1].map(t => {
        const x = labelW + t * chartW;
        return (
          <line key={t} x1={x} y1={0} x2={x} y2={svgH - 20}
            stroke="#E8EAF0" strokeWidth="1" strokeDasharray="4 3" />
        );
      })}
      {/* Zero line */}
      <line x1={labelW} y1={0} x2={labelW} y2={svgH - 20} stroke="#C8D0DC" strokeWidth="1.5" />

      {data.map((d, i) => {
        const y       = i * rowH + 4;
        const barLen  = Math.abs(d.value) / maxVal * chartW * 0.92;
        const color   = d.color || "#C87800";
        return (
          <g key={d.label}>
            {/* Label */}
            <text x={labelW - 8} y={y + barH / 2 + 4}
              textAnchor="end" fontSize={11} fill="#444" fontFamily="Segoe UI,system-ui,sans-serif">
              {d.label.length > 16 ? d.label.slice(0, 15) + "…" : d.label}
            </text>
            {/* Bar */}
            <rect x={labelW} y={y} width={barLen} height={barH} rx={3}
              fill={color} opacity={0.85} />
            {/* Value */}
            <text x={labelW + barLen + 6} y={y + barH / 2 + 4}
              fontSize={11} fill="#333" fontFamily="Segoe UI,system-ui,sans-serif" fontWeight="600">
              {fmt(d.value)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function EmptyChart() {
  return (
    <div style={{ height: 120, display: "flex", alignItems: "center", justifyContent: "center", color: "#BBB", fontSize: 13 }}>
      No data to display
    </div>
  );
}

// ── KPI card ────────────────────────────────────────────────────────────────────

function KPICard({ label, value, sub, color, icon: Icon }) {
  return (
    <div style={{ flex: 1, minWidth: 130, background: "#fff", border: `1.5px solid ${color}28`, borderRadius: 14, padding: "16px 18px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon style={{ color, fontSize: 18 }} />
        </div>
        <span style={{ fontSize: 22, fontWeight: 800, color: "#001A3D", lineHeight: 1, textAlign: "right" }}>{value}</span>
      </div>
      <span style={{ fontSize: 10, fontWeight: 700, color: "#888", letterSpacing: "0.6px", display: "block" }}>{label.toUpperCase()}</span>
      {sub && <span style={{ fontSize: 11, color, display: "block", marginTop: 2 }}>{sub}</span>}
    </div>
  );
}

// ── Chart card wrapper ──────────────────────────────────────────────────────────

function ChartCard({ title, sub, children, height }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #E4E8EF", borderRadius: 12, padding: "20px 24px" }}>
      <Text weight="semibold" style={{ color: "#001A3D", fontSize: 14, display: "block" }}>{title}</Text>
      {sub && <Text style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 16 }}>{sub}</Text>}
      <div style={{ marginTop: sub ? 0 : 12, minHeight: height || 200 }}>{children}</div>
    </div>
  );
}

// ── Summary Table ───────────────────────────────────────────────────────────────

function SummaryTable({ rows }) {
  if (!rows.length) return <EmptyChart />;
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr style={{ background: "#001A3D" }}>
            {["Category", "Phase", "Records", "Avg Awarded Rate", "Avg % Change", "Total Savings"].map(h => (
              <th key={h} style={{ padding: "10px 12px", color: "#C8D8FF", fontWeight: 700, fontSize: 10, letterSpacing: "0.5px", textAlign: h === "Category" || h === "Phase" ? "left" : "right", whiteSpace: "nowrap" }}>{h.toUpperCase()}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const ps = PHASE_STYLE[r.phase] || PHASE_STYLE[3];
            const cc = catColor(r.category);
            return (
              <tr key={r.category} style={{ borderBottom: "1px solid #EEF0F4", background: i % 2 === 0 ? "#fff" : "#FAFBFC" }}>
                <td style={{ padding: "10px 12px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 2, background: cc, flexShrink: 0 }} />
                    <span style={{ fontWeight: 600, color: "#001A3D" }}>{r.category}</span>
                  </div>
                </td>
                <td style={{ padding: "10px 12px" }}>
                  <span style={{ background: ps.bg, color: ps.color, padding: "2px 8px", borderRadius: 8, fontSize: 10, fontWeight: 700 }}>
                    {PHASE_LABELS[r.phase]}
                  </span>
                </td>
                <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700, color: "#333" }}>{r.count}</td>
                <td style={{ padding: "10px 12px", textAlign: "right", color: "#A66400", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                  {r.avgRate > 0 ? fmtNum(r.avgRate, 2) : "—"}
                </td>
                <td style={{ padding: "10px 12px", textAlign: "right" }}>
                  {r.avgPct !== null
                    ? <span style={{ color: r.avgPct < 0 ? "#107C10" : r.avgPct > 0 ? "#C4262E" : "#605E5C", fontWeight: 700 }}>
                        {r.avgPct > 0 ? "+" : ""}{r.avgPct.toFixed(1)}%
                      </span>
                    : <span style={{ color: "#AAA" }}>—</span>
                  }
                </td>
                <td style={{ padding: "10px 12px", textAlign: "right", color: "#107C10", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                  {r.totalSavings > 0 ? fmtNum(r.totalSavings, 0) : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Year-over-year table ────────────────────────────────────────────────────────

function YoYTable({ byYear }) {
  if (!byYear.length) return <EmptyChart />;
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr style={{ background: "#001A3D" }}>
            {["Year", "Records", "Avg Awarded Rate", "Avg % Change", "Total Savings"].map(h => (
              <th key={h} style={{ padding: "10px 12px", color: "#C8D8FF", fontWeight: 700, fontSize: 10, letterSpacing: "0.5px", textAlign: h === "Year" ? "left" : "right" }}>{h.toUpperCase()}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {byYear.map((r, i) => (
            <tr key={r.year} style={{ borderBottom: "1px solid #EEF0F4", background: i % 2 === 0 ? "#fff" : "#FAFBFC" }}>
              <td style={{ padding: "10px 12px", fontWeight: 700, color: "#001A3D" }}>{r.year}</td>
              <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700 }}>{r.count}</td>
              <td style={{ padding: "10px 12px", textAlign: "right", color: "#A66400", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                {r.avgRate > 0 ? fmtNum(r.avgRate, 2) : "—"}
              </td>
              <td style={{ padding: "10px 12px", textAlign: "right" }}>
                {r.avgPct !== null
                  ? <span style={{ color: r.avgPct < 0 ? "#107C10" : "#C4262E", fontWeight: 700 }}>
                      {r.avgPct > 0 ? "+" : ""}{r.avgPct.toFixed(1)}%
                    </span>
                  : <span style={{ color: "#AAA" }}>—</span>
                }
              </td>
              <td style={{ padding: "10px 12px", textAlign: "right", color: "#107C10", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                {r.totalSavings > 0 ? fmtNum(r.totalSavings, 0) : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Main analytics page ─────────────────────────────────────────────────────────

export default function BenchmarkAnalyticsPage() {
  const { records, loading, isDemoMode, refresh } = useBenchmarkDB();
  const navigate = useNavigate();

  const [filterPhase, setFilterPhase] = useState("All");
  const [filterYear,  setFilterYear]  = useState("All");
  const [activeTab,   setActiveTab]   = useState("charts");

  const years = useMemo(() => {
    const ys = new Set(records.map(r => r.tenderDate?.slice(0, 4)).filter(Boolean));
    return [...ys].sort().reverse();
  }, [records]);

  const filtered = useMemo(() => {
    return records.filter(r => {
      if (filterPhase !== "All" && String(r.phase) !== filterPhase) return false;
      if (filterYear  !== "All" && r.tenderDate?.slice(0, 4) !== filterYear) return false;
      return true;
    });
  }, [records, filterPhase, filterYear]);

  // Per-category summary
  const byCat = useMemo(() => {
    const map = {};
    filtered.forEach(r => {
      if (!map[r.category]) map[r.category] = { count: 0, rateSum: 0, pctSum: 0, pctCount: 0, savings: 0, phase: r.phase };
      map[r.category].count++;
      map[r.category].rateSum    += r.awardedRate || 0;
      map[r.category].savings    += r.costSavingAchieved || 0;
      if (r.benchmarkRate > 0) {
        map[r.category].pctSum   += r.percentageChange || 0;
        map[r.category].pctCount++;
      }
    });
    return Object.entries(map).map(([category, d]) => ({
      category,
      phase:        d.phase,
      count:        d.count,
      avgRate:      d.count > 0 ? d.rateSum / d.count : 0,
      avgPct:       d.pctCount > 0 ? d.pctSum / d.pctCount : null,
      totalSavings: d.savings,
    })).sort((a, b) => b.count - a.count);
  }, [filtered]);

  // Per-year summary
  const byYear = useMemo(() => {
    const map = {};
    filtered.forEach(r => {
      const y = r.tenderDate?.slice(0, 4);
      if (!y) return;
      if (!map[y]) map[y] = { count: 0, rateSum: 0, pctSum: 0, pctCount: 0, savings: 0 };
      map[y].count++;
      map[y].rateSum += r.awardedRate || 0;
      map[y].savings += r.costSavingAchieved || 0;
      if (r.benchmarkRate > 0) { map[y].pctSum += r.percentageChange || 0; map[y].pctCount++; }
    });
    return Object.entries(map).map(([year, d]) => ({
      year,
      count:        d.count,
      avgRate:      d.count > 0 ? d.rateSum / d.count : 0,
      avgPct:       d.pctCount > 0 ? d.pctSum / d.pctCount : null,
      totalSavings: d.savings,
    })).sort((a, b) => a.year.localeCompare(b.year));
  }, [filtered]);

  // Chart data
  const recordsByCat  = byCat.map(r => ({ label: r.category, value: r.count, color: catColor(r.category) }));
  const savingsByCat  = byCat.filter(r => r.totalSavings > 0).map(r => ({ label: r.category, value: r.totalSavings, color: catColor(r.category) }));
  const pctByCat      = byCat.filter(r => r.avgPct !== null).map(r => ({ label: r.category, value: r.avgPct, color: catColor(r.category) }));

  // KPI totals
  const totalRecords  = filtered.length;
  const totalSavings  = filtered.reduce((s, r) => s + (r.costSavingAchieved || 0), 0);
  const withPct       = filtered.filter(r => r.benchmarkRate > 0);
  const avgPct        = withPct.length > 0 ? withPct.reduce((s, r) => s + (r.percentageChange || 0), 0) / withPct.length : null;
  const cats          = new Set(filtered.map(r => r.category)).size;
  const primaryCurrency = records[0]?.currency || "NGN";

  const tabStyle = (active) => ({
    padding: "8px 18px",
    border: "none",
    borderBottom: active ? "2px solid #C87800" : "2px solid transparent",
    background: "none",
    color: active ? "#A66400" : "#888",
    fontWeight: active ? 700 : 400,
    fontSize: 13,
    cursor: "pointer",
  });

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 2px" }}>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Button appearance="subtle" icon={<ArrowLeft24Regular />} onClick={() => navigate("/benchmark")} style={{ color: "#C87800" }} />
          <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg,#A66400,#C87800)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ArrowTrendingLines24Regular style={{ color: "#fff", fontSize: 20 }} />
          </div>
          <div>
            <Text size={600} weight="bold" style={{ color: "#001A3D", lineHeight: 1.2, display: "block" }}>Pricing Analytics</Text>
            <Text style={{ fontSize: 12, color: "#888" }}>Rate trends, cost savings, and category intelligence</Text>
          </div>
          {isDemoMode && <span style={{ fontSize: 11, fontWeight: 700, color: "#E65C00", background: "#FFF4EC", border: "1px solid #FFD6A8", padding: "2px 10px", borderRadius: 20 }}>Demo Mode</span>}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Field>
            <Select value={filterPhase} onChange={(_, d) => setFilterPhase(d.value)} style={{ minWidth: 150 }}>
              <option value="All">All Phases</option>
              <option value="1">Phase 1 — High Value</option>
              <option value="2">Phase 2 — Mid Value</option>
              <option value="3">Phase 3 — Low Value</option>
            </Select>
          </Field>
          <Field>
            <Select value={filterYear} onChange={(_, d) => setFilterYear(d.value)} style={{ minWidth: 100 }}>
              <option value="All">All Years</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </Select>
          </Field>
          <Button appearance="subtle" icon={<ArrowSync24Regular />} onClick={refresh} />
        </div>
      </div>

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
          <Spinner size="large" label="Loading analytics..." />
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E4E8EF", padding: "60px 40px", textAlign: "center" }}>
          <Warning24Regular style={{ fontSize: 48, color: "#D0D7E5", marginBottom: 12 }} />
          <Text size={500} weight="bold" style={{ color: "#001A3D", display: "block", marginBottom: 8 }}>No Data Available</Text>
          <Text style={{ color: "#888" }}>
            {records.length === 0
              ? "Submit tender close-out records to see analytics."
              : "Try adjusting the phase or year filter."}
          </Text>
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
            <KPICard label="Total Records"   value={totalRecords} color="#C87800" icon={DataBarVertical24Regular} />
            <KPICard label="Categories"      value={cats}         color="#005A9E" icon={DataBarVertical24Regular} />
            <KPICard
              label="Total Cost Savings"
              value={totalSavings > 0 ? `${primaryCurrency} ${fmtNum(totalSavings)}` : "—"}
              color="#107C10"
              icon={ArrowTrendingLines24Regular}
              sub={totalSavings > 0 ? "Savings vs benchmark" : undefined}
            />
            <KPICard
              label="Avg % Change"
              value={avgPct !== null ? `${avgPct > 0 ? "+" : ""}${avgPct.toFixed(1)}%` : "—"}
              color={avgPct !== null && avgPct < 0 ? "#107C10" : "#C4262E"}
              icon={ArrowTrendingLines24Regular}
              sub={avgPct !== null ? (avgPct < 0 ? "Avg cost reduction" : "Avg cost increase") : "vs benchmark"}
            />
          </div>

          {/* Tabs */}
          <div style={{ borderBottom: "1px solid #E4E8EF", marginBottom: 20, display: "flex" }}>
            <button style={tabStyle(activeTab === "charts")} onClick={() => setActiveTab("charts")}>Charts</button>
            <button style={tabStyle(activeTab === "summary")} onClick={() => setActiveTab("summary")}>Category Summary</button>
            <button style={tabStyle(activeTab === "yearly")} onClick={() => setActiveTab("yearly")}>Year-over-Year</button>
          </div>

          {activeTab === "charts" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              <ChartCard title="Records by Category" sub="Count of benchmark records per procurement category" height={320}>
                <HBarChart data={recordsByCat} formatValue={v => v} />
              </ChartCard>

              <ChartCard title="Avg % Change vs Benchmark" sub="Green = rate decreased (saving); Red = rate increased" height={320}>
                <div style={{ overflowX: "auto" }}>
                  {pctByCat.length === 0 ? <EmptyChart /> : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {pctByCat.sort((a, b) => a.value - b.value).map(d => {
                        const color  = d.value < 0 ? "#107C10" : d.value > 0 ? "#C4262E" : "#888";
                        const pct    = d.value;
                        const maxAbs = Math.max(...pctByCat.map(x => Math.abs(x.value)), 1);
                        const barW   = Math.abs(pct) / maxAbs * 100;
                        return (
                          <div key={d.label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 11, color: "#555", minWidth: 120, textAlign: "right", flexShrink: 0 }}>
                              {d.label.length > 14 ? d.label.slice(0, 13) + "…" : d.label}
                            </span>
                            <div style={{ flex: 1, height: 22, background: "#F3F4F6", borderRadius: 4, overflow: "hidden", position: "relative" }}>
                              <div style={{ position: "absolute", top: 0, bottom: 0, left: pct < 0 ? `${50 - barW / 2}%` : "50%", width: `${barW / 2}%`, background: color, opacity: 0.8 }} />
                            </div>
                            <span style={{ fontSize: 11, color, fontWeight: 700, minWidth: 50, flexShrink: 0 }}>
                              {pct > 0 ? "+" : ""}{pct.toFixed(1)}%
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </ChartCard>

              <ChartCard title="Cost Savings by Category" sub={`${primaryCurrency} value of savings achieved vs benchmark`} height={280}>
                <HBarChart
                  data={savingsByCat.sort((a, b) => b.value - a.value)}
                  formatValue={v => `${fmtNum(v, 0)}`}
                />
              </ChartCard>

              <ChartCard title="Phase Distribution" sub="Record count by procurement phase" height={280}>
                <div style={{ display: "flex", flexDirection: "column", gap: 14, paddingTop: 8 }}>
                  {[1, 2, 3].map(ph => {
                    const ps    = PHASE_STYLE[ph];
                    const count = filtered.filter(r => r.phase === ph).length;
                    const pct2  = totalRecords > 0 ? (count / totalRecords) * 100 : 0;
                    return (
                      <div key={ph}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                          <span style={{ fontSize: 12, color: ps.color, fontWeight: 700 }}>{PHASE_LABELS[ph]}</span>
                          <span style={{ fontSize: 12, color: "#555" }}>{count} records ({pct2.toFixed(0)}%)</span>
                        </div>
                        <div style={{ height: 14, background: "#F3F4F6", borderRadius: 7, overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${pct2}%`, background: ps.color, borderRadius: 7, transition: "width 0.4s ease" }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ChartCard>
            </div>
          )}

          {activeTab === "summary" && (
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E4E8EF", overflow: "hidden" }}>
              <SummaryTable rows={byCat} />
              <div style={{ padding: "10px 16px", borderTop: "1px solid #EEF0F4", background: "#FAFBFC" }}>
                <Text style={{ fontSize: 12, color: "#888" }}>
                  {byCat.length} categor{byCat.length !== 1 ? "ies" : "y"} · {filtered.length} total records
                </Text>
              </div>
            </div>
          )}

          {activeTab === "yearly" && (
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E4E8EF", overflow: "hidden" }}>
              <YoYTable byYear={byYear} />
              <div style={{ padding: "10px 16px", borderTop: "1px solid #EEF0F4", background: "#FAFBFC" }}>
                <Text style={{ fontSize: 12, color: "#888" }}>
                  Historical price movement — {byYear.map(r => r.year).join(", ") || "no year data"}
                </Text>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
